import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription, finalize, forkJoin, map, of } from 'rxjs';
import { AvailableExamsService } from '../../../../api/api-services/available-exams.service';
import { MyOskLectureGroupService } from '../../../../api/api-services/my-osk-lecture-group.service';
import { MyOskLectureGroupArchiveService } from '../../../../api/api-services/my-osk-lecture-group-archive.service';
import { MyOskLectureGroupMembersService } from '../../../../api/api-services/my-osk-lecture-group-members.service';
import { MyOskStudentsService } from '../../../../api/api-services/my-osk-students.service';
import { IMyOskLectureGroupVm } from '../../../../api/my-osk-lecture-group-vm.model';
import { eModalRoles } from '../../../shared/models/modals';
import { MyOskEmployeesService } from '../../../../api/api-services/my-osk-employees.service';
import { isTeachingRole, OSK_EMPLOYEE_ROLE_LABELS } from '../../../../api/osk-employee-role.model';
import { canAdministerStudents } from '../../../shared/helpers/osk-staff-role';

/** Student pickers show the e-mail as the main label - a display name is optional and shown underneath. */
export interface StudentOption {
  label: string;
  value: string;
  name: string;
}

/** Students of the OSK are read in one go for the multiselect; OSKs are far below this size. */
export const LECTURE_GROUP_STUDENTS_PAGE_SIZE = 500;

@Component({
  selector: 'app-lecture-group-form',
  templateUrl: './lecture-group-form.component.html',
  styleUrl: './lecture-group-form.component.scss',
})
export class LectureGroupFormComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly availableExamsService = inject(AvailableExamsService);
  private readonly myOskStudentsService = inject(MyOskStudentsService);
  private readonly myOskLectureGroupService = inject(MyOskLectureGroupService);
  private readonly myOskLectureGroupArchiveService = inject(MyOskLectureGroupArchiveService);
  private readonly myOskLectureGroupMembersService = inject(MyOskLectureGroupMembersService);
  private readonly myOskEmployeesService = inject(MyOskEmployeesService);

  /** The boss and the office set the group up; instructors and lecturers only look at it. */
  readonly canManageGroups = canAdministerStudents();
  /** Instructors and lecturers of the OSK who can lead the group. */
  leaderOptions: { label: string; value: string }[] = [];

  /** The edited group; undefined when a new group is being created. */
  readonly group = this.dialogConfig.data?.group as IMyOskLectureGroupVm | undefined;
  /** Archived groups open read only, with the option to restore them. */
  readonly readonly = (this.dialogConfig.data?.readonly as boolean | undefined) ?? false;

  readonly form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(200)]],
    examGuid: ['', Validators.required],
    studentGuids: [[] as string[]],
    leaderEmployeeGuid: [null as string | null],
  });

  examCategoryOptions: { label: string; value: string }[] = [];
  studentOptions: StudentOption[] = [];
  loadingOptions = true;
  saving = false;

  private readonly subscriptions = new Subscription();

  get title(): string {
    if (this.readonly) {
      return 'Szczegóły grupy';
    }
    return this.group ? 'Edytuj grupę' : 'Dodaj grupę';
  }

  ngOnInit(): void {
    if (this.group) {
      this.form.patchValue({
        name: this.group.name,
        examGuid: this.group.examGuid,
        leaderEmployeeGuid: this.group.leaderEmployeeGuid ?? null,
      });
    }
    if (this.readonly) {
      this.form.disable();
    }
    this.loadOptions();
    if (this.canManageGroups) {
      this.loadLeaders();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  closeDialog(status: eModalRoles = eModalRoles.CLOSE): void {
    this.dialogRef.close(status);
  }

  save(): void {
    if (this.readonly || this.saving) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const request = this.group
      ? this.myOskLectureGroupService.put({
          guid: this.group.guid,
          name: (value.name ?? '').trim(),
          examGuid: value.examGuid ?? '',
          studentGuids: value.studentGuids ?? [],
          leaderEmployeeGuid: value.leaderEmployeeGuid || null,
        })
      : this.myOskLectureGroupService.post({
          name: (value.name ?? '').trim(),
          examGuid: value.examGuid ?? '',
          studentGuids: value.studentGuids ?? [],
          leaderEmployeeGuid: value.leaderEmployeeGuid || null,
        });

    this.saving = true;
    this.subscriptions.add(
      request.pipe(finalize(() => (this.saving = false))).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: this.group ? 'Grupa została zaktualizowana.' : 'Grupa została utworzona.',
          });
          this.closeDialog(eModalRoles.SUCCESS);
        },
        error: (error: HttpErrorResponse) => this.showError(error, 'Nie udało się zapisać grupy.'),
      })
    );
  }

  restore(): void {
    if (!this.group || this.saving) {
      return;
    }
    this.saving = true;
    this.subscriptions.add(
      this.myOskLectureGroupArchiveService
        .put({ guid: this.group.guid, isArchived: false })
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', detail: 'Grupa została przywrócona.' });
            this.closeDialog(eModalRoles.REFRESH);
          },
          error: (error: HttpErrorResponse) => this.showError(error, 'Nie udało się przywrócić grupy.'),
        })
    );
  }

  getErrorMessage(controlName: 'name' | 'examGuid'): string | null {
    const control = this.form.controls[controlName];
    if (!control.touched || !control.errors) {
      return null;
    }
    if (control.errors['required']) {
      return 'To pole jest wymagane.';
    }
    if (control.errors['maxlength']) {
      return 'Nazwa może mieć najwyżej 200 znaków.';
    }
    return null;
  }

  private loadLeaders(): void {
    this.subscriptions.add(
      this.myOskEmployeesService.get().subscribe({
        next: (employees) => {
          this.leaderOptions = employees
            .filter((employee) => !!employee.guid && isTeachingRole(employee.role))
            .map((employee) => ({
              label: `${employee.displayName} (${OSK_EMPLOYEE_ROLE_LABELS[employee.role]})`,
              value: employee.guid!,
            }));
        },
        // The leader is optional - a failed list only leaves the group without one.
        error: () => (this.leaderOptions = []),
      })
    );
  }

  private loadOptions(): void {
    const memberGuids$ = this.group
      ? this.myOskLectureGroupMembersService
          .get(this.group.guid)
          .pipe(map((members) => members.map((member) => member.guid)))
      : of([] as string[]);

    this.loadingOptions = true;
    this.subscriptions.add(
      forkJoin({
        exams: this.availableExamsService.get(),
        students: this.myOskStudentsService.get(false, '', 'created', 1, LECTURE_GROUP_STUDENTS_PAGE_SIZE),
        memberGuids: memberGuids$,
      })
        .pipe(finalize(() => (this.loadingOptions = false)))
        .subscribe({
          next: ({ exams, students, memberGuids }) => {
            this.examCategoryOptions = exams
              .filter((exam) => exam.active)
              .map((exam) => ({ label: exam.examName, value: exam.guid }));
            this.studentOptions = (students.data ?? []).map((student) => ({
              label: student.email,
              value: student.guid,
              name: (student.displayName ?? '').trim(),
            }));
            // A member who is no longer on the active student list still has to stay selectable.
            memberGuids
              .filter((guid) => !this.studentOptions.some((option) => option.value === guid))
              .forEach((guid) => this.studentOptions.push({ label: 'Kursant spoza aktywnej listy', value: guid, name: '' }));
            this.form.patchValue({ studentGuids: memberGuids });
            if (!this.group) {
              const defaultCategory = this.examCategoryOptions.find((option) => option.label.trim().toUpperCase() === 'B');
              if (defaultCategory) {
                this.form.patchValue({ examGuid: defaultCategory.value });
              }
            }
          },
          error: () =>
            this.messageService.add({
              severity: 'error',
              summary: 'Błąd',
              detail: 'Nie udało się pobrać kategorii lub listy kursantów.',
            }),
        })
    );
  }

  private showError(error: HttpErrorResponse, fallback: string): void {
    const apiMessage =
      typeof error.error === 'string' ? error.error : error.error?.message ?? error.error?.title;
    const detail = apiMessage?.includes('category cannot be changed while lectures are running')
      ? 'Nie można zmienić kategorii, gdy dla tej grupy trwają wykłady.'
      : apiMessage?.includes('Group name')
        ? 'Podaj poprawną nazwę grupy.'
        : fallback;
    this.messageService.add({ severity: 'error', summary: 'Błąd', detail });
  }
}
